/**
 * Styles for OnboardLoginScreen component
 * Responsive styles for mobile (xs) and desktop (md)
 */
import { designTokens } from '../../../../theme';

export const loginStyles = {
    pageContainer: {
        minHeight: '100vh',
        backgroundColor: designTokens.colors.background,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: 2, md: 4 },
    },
    logo: {
        marginBottom: { xs: 2, md: 3 },
    },
    logoSize: {
        width: { xs: 50, md: 64 },
        height: { xs: 50, md: 64 },
    },
    logoCircle: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: { xs: 50, md: 64 },
        height: { xs: 50, md: 64 },
    },
    logoIcon: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: 29, md: 42 },
        height: { xs: 22, md: 32 },
    },
    card: {
        backgroundColor: designTokens.colors.cardBackground,
        borderRadius: { xs: '16.5px', md: `${designTokens.spacing.cardBorderRadius}px` },
        padding: {
            xs: '22px 31px',
            md: `${designTokens.spacing.cardPaddingY}px ${designTokens.spacing.cardPaddingX}px`,
        },
        width: '100%',
        maxWidth: `${designTokens.spacing.cardWidth}px`,
        boxSizing: 'border-box',
        border: { xs: '0.55px solid rgba(102, 102, 102, 0.15)', md: 'none' },
    },
    title: {
        fontFamily: 'Inter, sans-serif !important',
        fontSize: { xs: '26px', md: '36px' },
        fontWeight: '600 !important',
        lineHeight: { xs: '26px', md: '36px' },
        letterSpacing: '-0.02em !important',
        color: `${designTokens.colors.textPrimary} !important`,
        textAlign: 'center',
        marginBottom: { xs: 2, md: 4 },
    },
    label: {
        fontFamily: 'Inter, sans-serif',
        fontSize: { xs: 12, md: designTokens.typography.label.fontSize },
        fontWeight: designTokens.typography.label.fontWeight,
        letterSpacing: designTokens.typography.label.letterSpacing,
        color: designTokens.colors.textSecondary,
        marginBottom: 0.5,
    },
    input: {
        '& .MuiOutlinedInput-root': {
            height: { xs: '40px', md: `${designTokens.spacing.inputHeight}px` },
            borderRadius: { xs: '6.6px', md: `${designTokens.spacing.inputBorderRadius}px` },
            backgroundColor: '#FFFFFF',
            overflow: 'hidden',
            '& fieldset': {
                borderColor: designTokens.colors.inputBorder,
            },
            '&:hover fieldset': {
                borderColor: designTokens.colors.divider,
            },
            '&.Mui-focused fieldset': {
                borderColor: designTokens.colors.primaryButton,
            },
        },
    },
    primaryButton: {
        backgroundColor: `${designTokens.colors.primaryButton} !important`,
        color: '#FFFFFF !important',
        borderRadius: { xs: '22px', md: '40px' },
        height: { xs: '35px', md: '64px' },
        fontSize: { xs: '14px', md: '22px' },
        fontWeight: '500 !important',
        textTransform: 'none !important',
        fontFamily: 'Inter, sans-serif !important',
        letterSpacing: '-0.02em',
        '& span, & .MuiButton-label': {
            color: '#FFFFFF !important',
        },
        '& .MuiButton-startIcon, & .MuiButton-endIcon, & .MuiCircularProgress-root': {
            color: '#FFFFFF !important',
            // color: '#FFFFFF !important', // if we want to have Log in button whote all time
            // opacity: 1,
            // '& span, & .MuiButton-label': {
            //     color: '#FFFFFF !important',
            // },
        },
        '&:hover': {
            backgroundColor: '#1a1a6e !important',
        },
        '&.Mui-disabled': {
            backgroundColor: `${designTokens.colors.primaryButton} !important`,
            color: 'rgba(255, 255, 255, 0.5) !important',
        },
    },
    secondaryButton: {
        backgroundColor: `${designTokens.colors.secondaryButton} !important`,
        color: '#FFFFFF !important',
        borderRadius: { xs: '22px', md: '40px' },
        height: { xs: '35px', md: '64px' },
        fontSize: { xs: '14px', md: '22px' },
        fontWeight: '500 !important',
        textTransform: 'none !important',
        fontFamily: 'Inter, sans-serif !important',
        letterSpacing: '-0.02em',
        '&:hover': {
            backgroundColor: '#e65588 !important',
        },
    },
    termsText: {
        fontFamily: 'Inter, sans-serif',
        fontSize: { xs: '9px', md: designTokens.typography.body.fontSize },
        fontWeight: designTokens.typography.body.fontWeight,
        letterSpacing: '-0.02em',
        color: designTokens.colors.textMuted,
        textAlign: 'center',
        marginTop: { xs: 1, md: 2 },
        lineHeight: { xs: '9px', md: 'normal' },
    },
    link: {
        color: 'rgba(97, 96, 99, 0.5) !important',
        textDecoration: 'underline !important',
        cursor: 'pointer',
        fontWeight: 400,
    },
    forgotPasswordLink: {
        fontFamily: 'Inter, sans-serif',
        fontSize: { xs: 10, md: designTokens.typography.body.fontSize },
        fontWeight: designTokens.typography.body.fontWeight,
        letterSpacing: '-0.02em',
        color: designTokens.colors.link,
        textDecoration: 'underline',
        cursor: 'pointer',
    },
    dividerContainer: {
        display: 'flex',
        alignItems: 'center',
        marginY: { xs: 2, md: 3 },
        width: '100%',
        maxWidth: `${designTokens.spacing.cardWidth}px`,
    },
    dividerLine: {
        flex: 1,
        height: '2px',
        backgroundColor: 'rgba(102, 102, 102, 0.25)',
        border: 'none',
    },
    dividerText: {
        fontFamily: 'Inter, sans-serif',
        fontSize: { xs: 10, md: designTokens.typography.divider.fontSize },
        fontWeight: designTokens.typography.divider.fontWeight,
        color: designTokens.colors.divider,
        paddingX: { xs: 2, md: 3 },
    },
};

// Helper to create full-width button with max-width constraint
export const getFullWidthButtonStyle = (baseStyle) => ({
    ...baseStyle,
    width: '100%',
    maxWidth: `${designTokens.spacing.cardWidth}px`,
});
